import { AbstractDocument, Identifiable, Timestampable } from '../../../abstract/Document';
import { Places } from '../../Places';
import type { ISupplierID } from '../../Suppliers.types';
import type { IAd } from '../../Suppliers/Ads.types';
import type { IAddonAdvertising as IAddon, IAddon_Definition } from './advertising.types';
import { collectionGroup, getDocs, limit, type Query, query, type QueryConstraint, where } from 'firebase/firestore';

@Identifiable
@Timestampable
export class AddonAdvertising extends AbstractDocument<IAddon> {
  readonly collections = {};

  /**
   * Returns the `Place` that contains this `Addon`.
   */
  get place() {
    return Places._.getById(this.reference.parent.parent!.id);
  }

  /**
   * Returns whether the addon can be claimed by the provided supplier type.
   */
  async isAvailable() {
    return false;
  }

  /**
   * Returns the most specific addon definition for the provided supplier type.
   *
   * @param type The supplier type, or `_` to target all types.
   * @param snapshot The `Addon` snapshot, optional.
   */
  async getDefinition<T extends keyof IAddon['suppliers']>(type?: T, snapshot?: IAddon) {
    if (snapshot == null) {
      snapshot = await this.get(true);
    }

    if (snapshot == null) {
      throw new Error(`Addon '${this.id}' doesn't exist for place '${this.place.id}'.`);
    }

    const { suppliers } = snapshot;

    if (type == null) {
      type = '_' as T;
    }

    /**
     * If we have T = venue, we want to return the most specific definition that's available:
     *
     * 1. [T]
     * 2. [_]
     */
    return (suppliers?.[type] ?? suppliers?.['_']) as IAddon_Definition;
  }

  /**
   * Returns supplier ads for this place.
   *
   * @param type The supplier type, or `_` to target all types.
   * @param maximum Maximum number of ads to return, or `null` to return everything. Defauls to 1.
   */
  async ads<T extends keyof IAddon['suppliers']>(type: T, maximum: number | null = 1): Promise<(IAd & { supplier: ISupplierID })[]> {
    const snapshot = await this.get(true);

    const ids = new Set<string>([snapshot?.suppliers?.[type]?.ads ?? [], snapshot?.suppliers?.['_']?.ads ?? []].flat());
    const result: (IAd & { supplier: ISupplierID })[] = [];

    /**
     * We want ads to be shown within all locations of a place.
     * If the current place doesn't have any ads defined, we traverse up the place ancestors, or down if the place has descendants.
     */
    if (ids.size === 0) {
      const place = await this.place.get(true);
      const ancestors = place?.ancestors ?? [];
      const descendants = place?.descendants ?? [];

      if (descendants.length > 0) {
        const result = Object.values(
          Object.fromEntries(
            (await Promise.all(descendants.map((id) => Places._.getById(id).Addons.advertising.ads(type, maximum))))
              .flat()
              .map((ad) => [ad.id, ad]),
          ),
        );

        if (result.length > 0) {
          return Number.isFinite(maximum) ? result.slice(0, maximum!) : result;
        }
      }

      if (ancestors.length > 0) {
        return await Places._.getById(ancestors.pop()!).Addons.advertising.ads(type, maximum);
      }

      return result;
    }

    const constraints: QueryConstraint[] = [where('id', 'in', [...ids])];

    if (Number.isFinite(maximum) === true) {
      constraints.push(limit(maximum!));
    }

    const ads = await getDocs(query(collectionGroup(this.reference.firestore, 'ads') as Query<IAd>, ...constraints));

    for (const document of ads.docs) {
      if (/^[/]?suppliers[/][^/]+[/]ads[/][^/]+$/.test(document.ref.path) !== true) {
        continue;
      }

      result.push({
        ...document.data(),
        supplier: document.ref.parent.parent!.id,
      });
    }

    return result;
  }
}
